import { Users, Loader2, LogIn, CheckCircle2 } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import MarketingLandingNav, { MARKETING_NAV_TOP_OFFSET } from '../components/marketing/MarketingLandingNav';
import { supabase } from '../lib/supabase';
import { SEO } from '../shared/components/SEO';
import { siteUrl } from '../shared/seo/siteOrigin';
import { getCurrentUserId } from '../utils/authUtils';

type CohortInfo = { id: string; name: string; purpose: string | null } | null;

interface JoinCohortPageProps {
  onNavigate: (view: string) => void;
}

const JoinCohortPage: React.FC<JoinCohortPageProps> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const token = typeof window !== 'undefined' ? window.location.pathname.replace(/^\/join\//, '').replace(/\/$/, '') : '';
  const [cohort, setCohort] = useState<CohortInfo>(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSignedIn, setIsSignedIn] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(t('views.joinCohort.invalidLink'));
      setLoading(false);
      return;
    }
    const fetchCohortAndCheckMembership = async () => {
      const { data, error: rpcError } = await supabase.rpc('get_public_cohort_by_token', {
        p_join_token: token,
      });
      if (rpcError || !data?.length) {
        setError(t('views.joinCohort.cohortNotFound'));
        setCohort(null);
        setLoading(false);
        return;
      }
      const row = data[0];
      const cohortData: CohortInfo = { id: row.id, name: row.name, purpose: row.purpose ?? null };
      setCohort(cohortData);

      // Check auth + existing membership in parallel so logged-in users never see the join button for a cohort they're already in
      const { data: { session } } = await supabase.auth.getSession();
      const uid = session?.user?.id;
      setIsSignedIn(!!uid);
      if (uid) {
        const { data: membership } = await supabase
          .from('collaborate_cohort_members')
          .select('cohort_id')
          .eq('cohort_id', cohortData.id)
          .eq('user_id', uid)
          .maybeSingle();
        if (membership) setJoined(true);
      }

      setLoading(false);
    };
    fetchCohortAndCheckMembership();

    // Keep isSignedIn in sync if the user logs in/out while on this page
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsSignedIn(!!session?.user);
    });
    return () => subscription.unsubscribe();
  }, [token, t]);

  const handleJoin = async () => {
    // Use getSession() (reads localStorage instantly) instead of getUser() (HTTP request)
    // to avoid false null returns during session initialisation.
    const { data: { session } } = await supabase.auth.getSession();
    const uid = session?.user?.id || await getCurrentUserId();
    if (!uid) {
      sessionStorage.setItem('redirect_after_login', window.location.pathname);
      onNavigate('login');
      return;
    }
    if (!cohort) return;
    setJoining(true);
    setError(null);
    const { error: insertErr } = await supabase.from('collaborate_cohort_members').insert({
      cohort_id: cohort.id,
      user_id: uid,
    });
    if (insertErr) {
      if (insertErr.code === '23505') {
        setJoined(true);
        setError(null);
      } else {
        setError(insertErr.message || t('views.joinCohort.couldNotJoin'));
      }
      setJoining(false);
      return;
    }
    setJoined(true);
    setJoining(false);

    const { data: userRow } = await supabase.from('users').select('name, email').eq('user_id', uid).single();
    const displayName = userRow?.name?.trim() || userRow?.email || t('views.joinCohort.aMember');
    await supabase.from('collaborate_cohort_messages').insert({
      cohort_id: cohort.id,
      user_id: null,
      message_type: 'system',
      content: `${displayName} ${t('views.joinCohort.joinedTheCohort')}`,
    });
  };

  const handleGoToCollaborate = () => {
    // Use a full navigation to '/collaborate' so AppStateManager correctly sets
    // currentView = 'dashboard' (it maps /collaborate → dashboard).
    // Never use onNavigate('collaborate') — it has no urlMap entry and falls to '/'.
    window.location.href = '/collaborate';
  };

  const Footer = () => (
    <footer className="flex-shrink-0 py-4 px-4 text-center border-t border-gray-200 dark:border-gray-700/80 bg-white dark:bg-gray-900">
      <p className="text-xs text-gray-500 dark:text-gray-400">
        {t('views.joinCohort.footerTagline', 'AI-powered resume optimization · Collaborate with your group')}
      </p>
      <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">
        © {new Date().getFullYear()} GoGrabJob. {t('views.joinCohort.allRightsReserved', 'All rights reserved.')}
      </p>
    </footer>
  );

  const seoBlock = (
    <SEO
      title="Join Cohort — GoGrabJob"
      description="Accept your invitation to join a GoGrabJob learning cohort and start working toward your career goals with a group."
      canonical={siteUrl(token ? `/join/${token}` : '/')}
      noIndex
    />
  );

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-950">
        {seoBlock}
        {!isSignedIn && <MarketingLandingNav onNavigate={onNavigate} variant="marketing" />}
        <div className={`flex-1 flex items-center justify-center p-6 ${!isSignedIn ? MARKETING_NAV_TOP_OFFSET : ''}`}>
          <Loader2 className="w-10 h-10 animate-spin" style={{ color: 'var(--brand-cta-bg)' }} />
        </div>
        <Footer />
      </div>
    );
  }

  if (error && !cohort) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-950">
        {seoBlock}
        {!isSignedIn && <MarketingLandingNav onNavigate={onNavigate} variant="marketing" />}
        <div className={`flex-1 flex items-center justify-center p-4 sm:p-6 ${!isSignedIn ? MARKETING_NAV_TOP_OFFSET : ''} pb-[env(safe-area-inset-bottom)]`}>
          <div className="text-center w-full max-w-sm bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-lg p-8">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
              <Users className="w-7 h-7 text-red-500 dark:text-red-400" />
            </div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
              {t('views.joinCohort.invalidOrExpired', 'Invalid or expired link')}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">{error}</p>
            <button
              type="button"
              onClick={() => onNavigate('home')}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-white font-medium transition-all min-h-[44px] touch-manipulation"
              style={{ background: 'var(--brand-cta-bg)' }}
            >
              <Home className="w-4 h-4" />
              {t('views.joinCohort.goToHome', 'Go to Home')}
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-950">
      {seoBlock}
      {!isSignedIn && <MarketingLandingNav onNavigate={onNavigate} variant="marketing" />}
      <div className={`flex-1 flex items-center justify-center p-4 sm:p-6 ${!isSignedIn ? MARKETING_NAV_TOP_OFFSET : ''} pb-[env(safe-area-inset-bottom)]`}>
        <div className="w-full max-w-md bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xl overflow-hidden">
          {/* Card header strip */}
          <div className="px-6 py-4 text-white" style={{ background: 'var(--brand-popup-header)' }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wider text-white/70">
                  {t('views.joinCohort.inviteToCohort', "You're invited to a group")}
                </p>
                <h1 className="text-lg font-bold text-white truncate">{cohort?.name}</h1>
              </div>
            </div>
          </div>

          {/* Card body */}
          <div className="p-6 sm:p-8">
            {cohort?.purpose && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 text-center leading-relaxed">{cohort.purpose}</p>
            )}
            {!cohort?.purpose && <div className="mb-2" />}

            {error && (
              <p className="text-sm text-amber-600 dark:text-amber-400 text-center mb-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg px-3 py-2">{error}</p>
            )}

            {joined ? (
              <div className="text-center">
                <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-green-500 dark:text-green-400" />
                </div>
                <p className="text-base font-semibold text-gray-900 dark:text-white mb-1">
                  {t('views.joinCohort.youreIn', "You're in this cohort.")}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                  {t('views.joinCohort.successHint', 'You have successfully joined the group.')}
                </p>
                <button
                  type="button"
                  onClick={handleGoToCollaborate}
                  className="w-full px-4 py-3 rounded-xl text-white font-semibold transition-all min-h-[44px] touch-manipulation hover:opacity-90"
                  style={{ background: 'var(--brand-cta-bg)' }}
                >
                  {t('views.joinCohort.openCollaborate', 'Open Collaborate')}
                </button>
              </div>
            ) : !isSignedIn ? (
              /* Not signed in — prompt them to log in first */
              <div className="text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
                  {t('views.joinCohort.signInToJoin', 'Sign in to join this group and start collaborating.')}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    sessionStorage.setItem('redirect_after_login', window.location.pathname);
                    onNavigate('login');
                  }}
                  className="w-full px-4 py-3 rounded-xl text-white font-semibold flex items-center justify-center gap-2 min-h-[44px] touch-manipulation transition-all hover:opacity-90"
                  style={{ background: 'var(--brand-cta-bg)' }}
                >
                  <LogIn className="w-5 h-5" />
                  {t('views.joinCohort.signInAndJoin', 'Sign in to join')}
                </button>
                <p className="text-xs text-gray-400 dark:text-gray-500 text-center mt-3">
                  {t('views.joinCohort.noAccountYet', "Don't have an account?")}{' '}
                  <button
                    type="button"
                    onClick={() => {
                      sessionStorage.setItem('redirect_after_login', window.location.pathname);
                      onNavigate('signup');
                    }}
                    className="underline text-purple-500 hover:text-purple-600 touch-manipulation"
                  >
                    {t('views.joinCohort.signUp', 'Sign up free')}
                  </button>
                </p>
              </div>
            ) : (
              /* Signed in — can join directly */
              <>
                <button
                  type="button"
                  onClick={handleJoin}
                  disabled={joining}
                  className="w-full px-4 py-3 rounded-xl text-white font-semibold disabled:opacity-50 flex items-center justify-center gap-2 min-h-[44px] touch-manipulation transition-all hover:opacity-90"
                  style={{ background: 'var(--brand-cta-bg)' }}
                >
                  {joining ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <LogIn className="w-5 h-5" />
                      {t('views.joinCohort.joinCohort', 'Join Group')}
                    </>
                  )}
                </button>
                <p className="text-xs text-gray-400 dark:text-gray-500 text-center mt-4">
                  {t('views.joinCohort.collaborateHint', 'You can access this group from the Collaborate tab in your dashboard.')}
                </p>
              </>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default JoinCohortPage;
